import { useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { CenterModal } from '../center-modal';
import styles from './thumbnail-crop-modal.module.scss';

interface ThumbnailCropModalProps {
  file: File;
  // The video's width / height: the thumbnail is cropped to the same shape so
  // it fills the player and the cards the way the video does.
  aspect: number;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (cropped: File) => void;
}

// Largest side of the saved thumbnail; the server makes its own smaller copies.
const MAX_OUTPUT_SIDE = 1920;
const MAX_ZOOM = 4;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

// Draws the chosen area into a JPEG file.
const cropToFile = async (url: string, area: Area): Promise<File> => {
  const image = await loadImage(url);
  const scale = Math.min(
    1,
    MAX_OUTPUT_SIDE / Math.max(area.width, area.height),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(area.width * scale);
  canvas.height = Math.round(area.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is not available');
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.9);
  });
  if (!blob) throw new Error('The image could not be cropped');
  return new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
};

export const ThumbnailCropModal = ({
  file,
  aspect,
  saving,
  onCancel,
  onConfirm,
}: ThumbnailCropModalProps) => {
  const [imageUrl, setImageUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [cropping, setCropping] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const busy = saving || cropping;

  const handleSave = async () => {
    if (!area || busy) return;
    setCropping(true);
    try {
      onConfirm(await cropToFile(imageUrl, area));
    } finally {
      setCropping(false);
    }
  };

  return (
    <CenterModal onClose={onCancel} className={styles.card}>
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.title}>Crop thumbnail</span>
          <span className={styles.subtitle}>
            Drag to position and zoom to fit. The frame matches the video&apos;s
            shape.
          </span>
        </div>

        <div className={styles.cropArea}>
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              maxZoom={MAX_ZOOM}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setArea(pixels)}
            />
          )}
        </div>

        <label className={styles.zoom} htmlFor="thumbnail-zoom">
          <span>Zoom</span>
          <input
            id="thumbnail-zoom"
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>

        <div className={styles.actions}>
          <Button
            buttonStyle={ButtonStyle.SecondaryGray}
            buttonSize={ButtonSize.Medium}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.Medium}
            onClick={handleSave}
            disabled={!area || busy}
          >
            {busy ? 'Saving…' : 'Save thumbnail'}
          </Button>
        </div>
      </div>
    </CenterModal>
  );
};
